import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daftu5ptto0ceqqnhhk0";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // honeypot: silently swallow bot submissions
    const honeypot = String(data.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed ? responseText : "");

      const isSpam = serverMsg.toLowerCase().includes("spam") || responseText.toLowerCase().includes("spam");

      if (!res.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  }

  return (
    <section id="contact" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1fr_1.1fr]">
        {/* left info */}
        <div>
          <SectionHeading
            eyebrow="Get In Touch"
            title="线索投递 · 订阅通讯"
            desc="掌握一手线索，或想第一时间收到我们的更新？告诉我们即可。所有投稿信息都会严格保密。"
          />
          <ul className="mt-7 space-y-4 text-sm text-foreground-700">
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-mail-send-line"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-900">邮件联络</p>
                <p className="mt-0.5 text-foreground-600">contact@guaguabaoliao.example</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-shield-user-line"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-900">隐私承诺</p>
                <p className="mt-0.5 text-foreground-600">
                  投稿人信息不公开、不转售，线索核实前不会对外发布。
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-time-line"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-900">响应时间</p>
                <p className="mt-0.5 text-foreground-600">工作日 48 小时内邮件回复进度。</p>
              </div>
            </li>
          </ul>
        </div>

        {/* form */}
        <form
          onSubmit={handleSubmit}
          data-readdy-form="线索投稿与订阅"
          noValidate
          className="rounded-lg border border-background-300 bg-background-50 p-6 md:p-8"
        >
          <h3 className="font-heading text-lg font-bold text-foreground-950">在线提交表单</h3>
          <p className="mt-1 text-xs text-foreground-500">
            选择『投递线索』或『订阅通讯』，带 * 为必填。
          </p>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="purpose" className="mb-1.5 block text-sm font-medium text-foreground-800">
                意向类型 *
              </label>
              <select
                id="purpose"
                name="purpose"
                required
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              >
                <option value="">请选择</option>
                <option value="投递行业线索">投递行业线索</option>
                <option value="订阅每日通讯">订阅每日通讯</option>
                <option value="商务合作">商务合作</option>
                <option value="其他咨询">其他咨询</option>
              </select>
            </div>
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                您的称呼 *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="如何称呼您"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
              联系邮箱 *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="mt-4">
            <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-foreground-800">
              留言 / 线索描述
            </label>
            <textarea
              id="content"
              name="content"
              rows={5}
              maxLength={500}
              placeholder="请尽量附上时间、涉及主体与初步证据（最多 500 字）"
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            ></textarea>
          </div>

          {/* honeypot */}
          <input
            type="text"
            name="company_alt"
            className="form-honeypot"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === "error" && formError && (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
              {formError}
            </div>
          )}
          {status === "success" && (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0"></i>
              已收到！我们会在 48 小时内与你联系。感谢你的信任。
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i> 提交中…
              </>
            ) : (
              <>
                提交表单 <i className="ri-send-plane-line"></i>
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}